import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSnackbar } from 'notistack';
import { SageLinkChip } from './SageLinkChip';
import { ConfirmDialog } from './ConfirmDialog';
import { SageLinkCustomerDialog } from './SageLinkCustomerDialog';
import { sageCustomerLinkingService } from '../../services/sageCustomerLinkingService';
import { sageEntityLinkService } from '../../services/sageEntityLinkService';
import {
  isSageCustomerConflict,
  type SageContactDetails,
  type SageCustomerDetails,
  type SageEntityLinkDto,
} from '../../types/sage';
import { getFriendlyErrorMessage } from '../../utils/errorUtils';

interface SageDetailsSectionErrors {
  sageName?: string;
  taxReference?: string;
}

interface SageDetailsSectionProps {
  sageName?: string;
  taxReference?: string;
  suggestedSageName?: string;
  isSageReady?: boolean;
  isLinkedToSage?: boolean;
  isCustomer?: boolean;
  isSupplier?: boolean;
  onSageNameChange: (value: string) => void;
  onTaxReferenceChange: (value: string) => void;
  disabled?: boolean;
  errors?: SageDetailsSectionErrors;
  entityType?: string;
  entityId?: number;
  /** Supplier flow (Labs): opens the caller-owned supplier picker. Ignored when `customerLinking`. */
  onFindInSage?: () => void;
  findInSageLoading?: boolean;
  /**
   * Customer flow (Farmers/Agents): enables the create/link/sync/relink/unlink actions row,
   * managed entirely inside this section (picker + live link status).
   */
  customerLinking?: boolean;
  /** Called after any successful customer action so the parent dialog can flag a change. */
  onSageChanged?: () => void;
}

/**
 * Section for Sage accounting details.
 *
 * Header shows a single status chip via {@link SageLinkChip} — "Not set" / "Ready to create"
 * (local metadata only, NOT yet in Sage) / "Customer" / "Supplier" / "Customer & Supplier".
 *
 * When `customerLinking` is set (Farmers/Agents), it also owns a live actions/status row:
 *   - not-linked + ready → "Create in Sage" (ensure-customer; 409 opens the picker) and
 *     "Find / link in Sage" (opens the searchable picker).
 *   - linked → Sage Customer ID (+ copy), last-synced time, a sync-status chip, and
 *     "Sync now" / "Relink" / "Unlink" actions.
 * Link status is fetched on mount and re-fetched after every action.
 *
 * The supplier flow (Labs) keeps the caller-owned `onFindInSage` button unchanged.
 */
export const SageDetailsSection: React.FC<SageDetailsSectionProps> = ({
  sageName = '',
  taxReference = '',
  suggestedSageName = '',
  isSageReady = false,
  isLinkedToSage = false,
  isCustomer = false,
  isSupplier = false,
  onSageNameChange,
  onTaxReferenceChange,
  disabled = false,
  errors,
  entityType,
  entityId,
  onFindInSage,
  findInSageLoading = false,
  customerLinking = false,
  onSageChanged,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [link, setLink] = useState<SageEntityLinkDto | null>(null);
  const [fetched, setFetched] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState<'create' | 'sync' | 'unlink' | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSeed, setPickerSeed] = useState<{ term?: string; candidate?: SageCustomerDetails | null }>({});
  const [confirmUnlinkOpen, setConfirmUnlinkOpen] = useState(false);

  const canManage = !!customerLinking && !!entityType && !!entityId;

  const refreshLink = useCallback(async () => {
    if (!customerLinking || !entityType || !entityId) return;
    setLinkLoading(true);
    try {
      const status = await sageEntityLinkService.getLinkStatus(entityType, entityId);
      setLink(status);
      setFetched(true);
    } catch {
      // Transient read error — keep whatever we last knew rather than blanking the section.
    } finally {
      setLinkLoading(false);
    }
  }, [customerLinking, entityType, entityId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async fetch-on-mount; loading flag is set synchronously by refreshLink
    void refreshLink();
  }, [refreshLink]);

  // Derived link status: prefer the freshly-fetched link once we've fetched; otherwise fall
  // back to the props passed by the parent (accurate on first paint, e.g. an already-linked farmer).
  const useFetched = canManage && fetched;
  const chipDetails: SageContactDetails = {
    isSageReady: !!isSageReady,
    isLinkedToSage: useFetched
      ? !!(link && link.isActive && (link.sageCustomerId || link.sageSupplierId))
      : !!isLinkedToSage,
    isCustomer: useFetched
      ? !!(link && link.isActive && link.sageCustomerId)
      : !!isCustomer,
    isSupplier: useFetched
      ? !!(link && link.isActive && link.sageSupplierId)
      : !!isSupplier,
  };
  const isLinked = chipDetails.isLinkedToSage;

  const handleGenerateSageName = () => {
    if (suggestedSageName) {
      onSageNameChange(suggestedSageName);
    }
  };

  const openPicker = (seed: { term?: string; candidate?: SageCustomerDetails | null }) => {
    setPickerSeed(seed);
    setPickerOpen(true);
  };

  const handleCreate = useCallback(async () => {
    if (!entityType || !entityId) return;
    setActionBusy('create');
    try {
      await sageEntityLinkService.ensureCustomer(entityType, entityId);
      enqueueSnackbar('Customer created in Sage', { variant: 'success' });
      await refreshLink();
      onSageChanged?.();
    } catch (err) {
      const conflict = isSageCustomerConflict(err);
      if (conflict) {
        setPickerSeed({
          term: conflict.customerName,
          candidate: {
            id: conflict.sageCustomerId,
            name: conflict.customerName,
            active: true,
            balance: 0,
          },
        });
        setPickerOpen(true);
      } else {
        enqueueSnackbar(getFriendlyErrorMessage(err, 'Failed to create customer in Sage'), { variant: 'error' });
      }
    } finally {
      setActionBusy(null);
    }
  }, [entityType, entityId, enqueueSnackbar, refreshLink, onSageChanged]);

  const handleSync = useCallback(async () => {
    if (!entityType || !entityId) return;
    setActionBusy('sync');
    try {
      await sageEntityLinkService.syncNow(entityType, entityId);
      enqueueSnackbar('Sync complete', { variant: 'success' });
      onSageChanged?.();
    } catch (err) {
      enqueueSnackbar(getFriendlyErrorMessage(err, 'Sync failed'), { variant: 'error' });
    } finally {
      // Refresh regardless so the sync-status chip reflects success or failure.
      await refreshLink();
      setActionBusy(null);
    }
  }, [entityType, entityId, enqueueSnackbar, refreshLink, onSageChanged]);

  const handleUnlink = useCallback(async () => {
    if (!entityType || !entityId) return;
    setActionBusy('unlink');
    try {
      await sageCustomerLinkingService.unlinkCustomer(entityType, entityId);
      enqueueSnackbar('Customer unlinked', { variant: 'success' });
      await refreshLink();
      onSageChanged?.();
    } catch (err) {
      enqueueSnackbar(getFriendlyErrorMessage(err, 'Failed to unlink customer'), { variant: 'error' });
    } finally {
      setActionBusy(null);
      setConfirmUnlinkOpen(false);
    }
  }, [entityType, entityId, enqueueSnackbar, refreshLink, onSageChanged]);

  const handlePickerLinked = useCallback(async () => {
    setPickerOpen(false);
    await refreshLink();
    onSageChanged?.();
  }, [refreshLink, onSageChanged]);

  const handleCopyId = useCallback(async (id?: string) => {
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      enqueueSnackbar('Sage Customer ID copied', { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not copy to clipboard', { variant: 'warning' });
    }
  }, [enqueueSnackbar]);

  const busy = disabled || actionBusy !== null;
  const syncStatus = link?.lastSyncStatus;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Sage Account Details
        </Typography>
        <SageLinkChip sageDetails={chipDetails} />
        {linkLoading && <CircularProgress size={14} />}
        {/* Supplier flow (Labs) keeps its caller-owned find button. */}
        {!customerLinking && entityId && !isLinked && isSageReady && onFindInSage && (
          <Button
            size="small"
            variant="outlined"
            startIcon={findInSageLoading ? <CircularProgress size={14} /> : <SearchIcon />}
            onClick={onFindInSage}
            disabled={disabled || findInSageLoading}
            sx={{ whiteSpace: 'nowrap', height: 28 }}
          >
            Find in Sage
          </Button>
        )}
      </Box>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, sm: 5 }}>
          <TextField
            fullWidth
            size="small"
            label="Sage Name"
            value={sageName}
            onChange={(e) => onSageNameChange(e.target.value)}
            disabled={disabled}
            inputProps={{ maxLength: 200 }}
            error={!!errors?.sageName}
            helperText={errors?.sageName || 'Unique name used for Sage Customer/Supplier creation'}
          />
        </Grid>
        <Grid size="auto" sx={{ pt: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoFixHighIcon />}
            onClick={handleGenerateSageName}
            disabled={disabled || !suggestedSageName}
            sx={{ whiteSpace: 'nowrap', height: 40 }}
          >
            Generate
          </Button>
        </Grid>
        <Grid size={{ xs: 12, sm: "grow" }}>
          <TextField
            fullWidth
            size="small"
            label="Tax Reference"
            value={taxReference}
            onChange={(e) => onTaxReferenceChange(e.target.value)}
            disabled={disabled}
            inputProps={{ maxLength: 50 }}
            error={!!errors?.taxReference}
            helperText={errors?.taxReference}
          />
        </Grid>
      </Grid>

      {/* Customer flow (Farmers/Agents): live actions + sync status. */}
      {canManage && (
        <Box
          sx={(theme) => ({
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50',
            ...theme.applyStyles('dark', { backgroundColor: 'grey.900' }),
          })}
        >
          {isLinked ? (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  Sage Customer ID:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {link?.sageCustomerId ?? '—'}
                </Typography>
                {link?.sageCustomerId && (
                  <Tooltip title="Copy ID">
                    <IconButton size="small" onClick={() => handleCopyId(link?.sageCustomerId)}>
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  {link?.lastSyncedAt
                    ? `Last synced ${new Date(link.lastSyncedAt).toLocaleString()}`
                    : 'Never synced'}
                </Typography>
                {syncStatus === 'Success' && (
                  <Chip size="small" color="success" variant="outlined" label="Synced" />
                )}
                {syncStatus === 'Failed' && (
                  <Tooltip title={link?.lastSyncError || 'Sync failed'}>
                    <Chip size="small" color="error" variant="outlined" label="Sync failed" />
                  </Tooltip>
                )}
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={actionBusy === 'sync' ? <CircularProgress size={14} /> : <SyncIcon />}
                  onClick={handleSync}
                  disabled={busy}
                >
                  Sync now
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={() => openPicker({ term: sageName })}
                  disabled={busy}
                >
                  Relink
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={actionBusy === 'unlink' ? <CircularProgress size={14} /> : <LinkOffIcon />}
                  onClick={() => setConfirmUnlinkOpen(true)}
                  disabled={busy}
                >
                  Unlink
                </Button>
              </Stack>
            </Stack>
          ) : isSageReady ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <Button
                size="small"
                variant="contained"
                startIcon={actionBusy === 'create' ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
                onClick={handleCreate}
                disabled={busy}
                sx={{ textTransform: 'none' }}
              >
                Create in Sage
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => openPicker({ term: sageName })}
                disabled={busy}
                sx={{ textTransform: 'none' }}
              >
                Find / link in Sage
              </Button>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Enter a Sage Name above to create or link this record in Sage.
            </Typography>
          )}
        </Box>
      )}

      {canManage && entityType && entityId && (
        <SageLinkCustomerDialog
          open={pickerOpen}
          entityType={entityType}
          entityId={entityId}
          initialSearch={pickerSeed.term}
          initialCandidate={pickerSeed.candidate}
          onClose={() => setPickerOpen(false)}
          onLinked={handlePickerLinked}
        />
      )}

      <ConfirmDialog
        open={confirmUnlinkOpen}
        title="Unlink Sage customer?"
        message="This removes the link between this record and its Sage customer. The Sage customer itself is not deleted — you can re-link at any time."
        confirmLabel="Unlink"
        confirmColor="error"
        loading={actionBusy === 'unlink'}
        onConfirm={handleUnlink}
        onCancel={() => setConfirmUnlinkOpen(false)}
      />
    </Box>
  );
};

export default SageDetailsSection;
